import {
  Alert,
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
  alpha,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core'
import { TbAlertTriangle, TbArchive, TbChevronDown, TbChevronRight, TbClock, TbX } from 'react-icons/tb'
import { CSSProperties, Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { orderBy } from 'lodash-es'

import PageShell from '@/components/Common/PageShell'
import {
  getArchivedUnexpectedEventsOptions,
  getArchivedUnexpectedEventsQueryKey,
  getPendingUnexpectedEventsOptions,
  getPendingUnexpectedEventsQueryKey,
  resolveUnexpectedEventMutation,
  unresolveUnexpectedEventMutation,
} from '@/api/main/@tanstack/react-query.gen'
import { UnexpectedEventResponse, UnexpectedEventType } from '@/api/main/types.gen'
import { Route as UnexpectedEventsRoute, UnexpectedEventsTab } from '@/routes/unexpected-events'
import { UNEXPECTED_EVENT_TYPES } from '@/components/UnexpectedEvents/eventTypes'
import { toPragueLocaleString } from '@/utils/datetime'
import { useAuthStore } from '@/stores/auth'
import { hasPermission } from '@/utils/permissions'
import CopyableId from './CopyableId'

const HIDDEN_TYPES: ReadonlySet<UnexpectedEventType> = new Set([
  'road_temporarily_blocked',
  'inaccurate_gps',
  'custom_event_1',
  'custom_event_2',
  'custom_event_3',
  'custom_event_4',
])

const TYPE_LABEL_KEY: Record<UnexpectedEventType, string> = {
  unknown: 'unknown',
  turn_not_possible: 'turnNotPossible',
  road_temporarily_blocked: 'roadTemporarilyBlocked',
  road_closed: 'roadClosed',
  inaccurate_map: 'inaccurateMap',
  inaccurate_gps: 'inaccurateGps',
  custom_event_1: 'unknown',
  custom_event_2: 'unknown',
  custom_event_3: 'unknown',
  custom_event_4: 'unknown',
}

const TYPE_BADGE_COLOR: Record<UnexpectedEventType, string> = {
  unknown: 'gray',
  turn_not_possible: 'orange',
  road_temporarily_blocked: 'yellow',
  road_closed: 'red',
  inaccurate_map: 'grape',
  inaccurate_gps: 'gray',
  custom_event_1: 'gray',
  custom_event_2: 'gray',
  custom_event_3: 'gray',
  custom_event_4: 'gray',
}

const districtLabel = (event: UnexpectedEventResponse) => `${event.district_code}-${event.subdistrict_code}`

const sortByObservedDesc = (events: UnexpectedEventResponse[]) => orderBy(events, ['observed_at'], ['desc'])
const sortRoutesAsc = (entries: [string, UnexpectedEventResponse[]][]) =>
  orderBy(entries, [([routeId]) => routeId], ['asc'])

// Stable references hoisted out of the row so memoized rows aren't invalidated on every parent render.
const KEEP_SEARCH = <T,>(prev: T): T => prev

const LIST_GRID_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  gap: '4px',
}
const LIST_GRID_WITH_CHECKBOX: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto auto 1fr auto',
  gap: '4px',
}
const FULL_SPAN: CSSProperties = { gridColumn: '1 / -1' }
const ROW_SUBGRID: CSSProperties = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignItems: 'center',
  gap: 'var(--mantine-spacing-md)',
}
const ROW_PAPER_BASE: CSSProperties = {
  cursor: 'pointer',
  transition: 'transform 120ms ease, box-shadow 120ms ease',
}
const ROW_PAPER_HOVER: CSSProperties = {
  transform: 'translateY(-2px)',
  boxShadow: 'var(--mantine-shadow-sm)',
}
const LINK_STYLE: CSSProperties = {
  color: 'var(--mantine-color-blue-6)',
  textDecoration: 'underline',
  fontFamily: 'var(--mantine-font-family-monospace)',
}

type EnrichedEvent = {
  id: string
  event: UnexpectedEventResponse
  formattedObservedAt: string
  typeLabel: string
  badgeColor: string
}

type RouteGroup = {
  routeId: string
  ids: ReadonlyArray<string>
  events: ReadonlyArray<EnrichedEvent>
}

const UnexpectedEventsList = () => {
  const { t } = useTranslation('unexpectedEvents')
  const search = UnexpectedEventsRoute.useSearch()
  const navigate = useNavigate({ from: '/unexpected-events' })
  const queryClient = useQueryClient()
  const { userPermissions } = useAuthStore()

  const canWrite = hasPermission(userPermissions, 'unexpected_events', 'write')

  const activeTab: UnexpectedEventsTab = search.tab === 'archived' ? 'archived' : 'pending'
  const isArchive = activeTab === 'archived'

  const pendingQuery = useQuery(getPendingUnexpectedEventsOptions())
  const archivedQuery = useQuery(getArchivedUnexpectedEventsOptions())
  const eventsQuery = isArchive ? archivedQuery : pendingQuery

  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set())
  const [collapsedRoutes, setCollapsedRoutes] = useState<Set<string>>(new Set())

  const visibleEvents = useMemo(
    () => (eventsQuery.data?.events ?? []).filter(event => !HIDDEN_TYPES.has(event.type)),
    [eventsQuery.data]
  )

  // District counts drive the picker labels so the user can see where the work is
  // before they even open the dropdown.
  const districtOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const event of visibleEvents) {
      const key = districtLabel(event)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([value, count]) => ({ value, label: `${value} (${count})` }))
  }, [visibleEvents])

  // Only the events for the selected district(s) matter for the rest of the page.
  const selectedDistricts = useMemo(() => new Set(search.district ?? []), [search.district])
  const hasDistrictFilter = selectedDistricts.size > 0

  const districtEvents = useMemo(() => {
    if (!hasDistrictFilter) return []
    return visibleEvents.filter(event => selectedDistricts.has(districtLabel(event)))
  }, [visibleEvents, selectedDistricts, hasDistrictFilter])

  const routeOptions = useMemo(() => {
    const set = new Set<string>()
    for (const event of districtEvents) set.add(event.route_id)
    return Array.from(set).sort()
  }, [districtEvents])

  const typeOptions = useMemo(
    () =>
      UNEXPECTED_EVENT_TYPES.filter(type => !HIDDEN_TYPES.has(type)).map(type => ({
        value: type,
        label: t(TYPE_LABEL_KEY[type]),
      })),
    [t]
  )

  const filteredEvents = useMemo(() => {
    if (!hasDistrictFilter) return []
    return districtEvents.filter(event => {
      if (search.route && event.route_id !== search.route) return false
      if (search.type && event.type !== search.type) return false
      return true
    })
  }, [districtEvents, hasDistrictFilter, search.route, search.type])

  const pageTitle = useMemo(() => {
    const base = t('title')
    if (!eventsQuery.data) return base
    if (!hasDistrictFilter) return visibleEvents.length > 0 ? `${base} (${visibleEvents.length})` : base
    const districts = (search.district ?? []).join(', ')
    return `${base} · ${districts} (${filteredEvents.length})`
  }, [t, eventsQuery.data, hasDistrictFilter, visibleEvents.length, filteredEvents.length, search.district])

  // Drop selected event IDs that no longer appear in the filtered set so the user
  // doesn't unknowingly resolve events that scrolled out of view after a filter change.
  const filteredIdSet = useMemo(() => new Set(filteredEvents.map(e => e.id)), [filteredEvents])
  const prevFilteredRef = useRef(filteredIdSet)
  useEffect(() => {
    if (prevFilteredRef.current === filteredIdSet) return
    prevFilteredRef.current = filteredIdSet
    setSelectedEvents(prev => {
      const next = new Set<string>()
      for (const id of prev) {
        if (filteredIdSet.has(id)) next.add(id)
      }
      return next.size === prev.size ? prev : next
    })
  }, [filteredIdSet])

  // Group the (already district-scoped) events by route so we can render section
  // headings instead of a deeply nested NavLink tree.
  // Pre-format per-row strings here so each row doesn't re-run Intl/i18n on every render.
  const routeGroups = useMemo<ReadonlyArray<RouteGroup>>(() => {
    const byRoute = new Map<string, UnexpectedEventResponse[]>()
    for (const event of filteredEvents) {
      const arr = byRoute.get(event.route_id) ?? []
      arr.push(event)
      byRoute.set(event.route_id, arr)
    }
    const sorted = sortRoutesAsc(
      Array.from(byRoute.entries()).map(([routeId, events]) => [routeId, sortByObservedDesc(events)])
    )
    return sorted.map(([routeId, events]): RouteGroup => {
      const enriched: EnrichedEvent[] = events.map(event => ({
        id: event.id,
        event,
        formattedObservedAt: toPragueLocaleString(new Date(event.observed_at)),
        typeLabel: t(TYPE_LABEL_KEY[event.type]),
        badgeColor: TYPE_BADGE_COLOR[event.type],
      }))
      return {
        routeId,
        events: enriched,
        ids: enriched.map(e => e.id),
      }
    })
  }, [filteredEvents, t])

  const updateSearchFilter = (patch: Partial<typeof search>) => {
    navigate({
      to: '/unexpected-events',
      search: prev => ({ ...prev, ...patch }),
      replace: true,
    })
  }

  const toggleEventSelection = useCallback((ids: ReadonlyArray<string>, selected: boolean) => {
    setSelectedEvents(prev => {
      const next = new Set(prev)
      if (selected) ids.forEach(id => next.add(id))
      else ids.forEach(id => next.delete(id))
      return next
    })
  }, [])

  // Stable per-row toggle so memoized rows don't see a fresh function identity each render.
  const handleRowToggle = useCallback((id: string, selected: boolean) => {
    setSelectedEvents(prev => {
      const next = new Set(prev)
      if (selected) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const { mutateAsync: resolveAsync, isPending: isResolving } = useMutation(resolveUnexpectedEventMutation())

  const { mutateAsync: unresolveAsync, isPending: isUnresolving } = useMutation(unresolveUnexpectedEventMutation())

  const performGroupAction = async (ids: ReadonlyArray<string>, action: 'resolve' | 'unresolve') => {
    if (ids.length === 0) return
    const mutateAsync = action === 'resolve' ? resolveAsync : unresolveAsync
    const successMsg = action === 'resolve' ? t('header.resolveSuccess') : t('header.unresolveSuccess')
    const results = await Promise.allSettled(ids.map(eventId => mutateAsync({ path: { event_id: eventId } })))
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected').length
    setSelectedEvents(new Set())
    await queryClient.invalidateQueries({ queryKey: getPendingUnexpectedEventsQueryKey() })
    await queryClient.invalidateQueries({ queryKey: getArchivedUnexpectedEventsQueryKey() })
    if (failed === 0) {
      notifications.show({ message: successMsg, color: 'green' })
    } else if (failed === ids.length) {
      notifications.show({
        message: action === 'resolve' ? t('header.resolveError') : t('header.unresolveError'),
        color: 'red',
      })
    } else {
      notifications.show({
        message: t('list.partialBulkFailure', { failed, total: ids.length }),
        color: 'yellow',
      })
    }
  }

  const openGroupActionConfirm = () => {
    const ids = Array.from(selectedEvents)
    if (ids.length === 0) return
    const idSet = new Set(ids)
    const events = visibleEvents.filter(e => idSet.has(e.id))
    const actionType = isArchive ? 'unresolve' : 'resolve'
    modals.openConfirmModal({
      title: isArchive
        ? t('confirm.unresolveBulkTitle', { count: ids.length })
        : t('confirm.resolveBulkTitle', { count: ids.length }),
      centered: true,
      size: 'xl',
      children: (
        <Stack gap="sm">
          <Text size="sm">{isArchive ? t('confirm.unresolveBulkBody') : t('confirm.resolveBulkBody')}</Text>
          <ResolveBulkPreview events={events} t={t} />
        </Stack>
      ),
      labels: { confirm: t('confirm.confirm'), cancel: t('confirm.cancel') },
      confirmProps: { color: 'blue' },
      onConfirm: () => performGroupAction(ids, actionType),
    })
  }

  const isMutating = isResolving || isUnresolving
  const action = canWrite ? (
    <Button color="blue" size="xs" disabled={selectedEvents.size === 0 || isMutating} onClick={openGroupActionConfirm}>
      {isArchive
        ? t('list.groupUnresolve', { count: selectedEvents.size })
        : t('list.groupResolve', { count: selectedEvents.size })}
    </Button>
  ) : undefined

  const noEventsMessage = isArchive ? t('noArchivedEvents') : t('noPendingEvents')
  const districtPlaceholder = eventsQuery.isLoading
    ? t('list.loading')
    : districtOptions.length === 0
      ? noEventsMessage
      : t('list.selectDistrictPlaceholder')

  const handleTabChange = (tab: string | null) => {
    setSelectedEvents(new Set())
    setCollapsedRoutes(new Set())
    navigate({
      to: '/unexpected-events',
      search: prev => ({
        ...prev,
        tab: tab === 'archived' ? ('archived' as const) : undefined,
      }),
      replace: true,
    })
  }

  return (
    <PageShell title={pageTitle} action={action}>
      <Stack gap="md">
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="pending" leftSection={<TbClock size={16} />}>
              {t('tabPending')}
              {pendingQuery.data &&
                ` (${(pendingQuery.data.events ?? []).filter(e => !HIDDEN_TYPES.has(e.type)).length})`}
            </Tabs.Tab>
            <Tabs.Tab value="archived" leftSection={<TbArchive size={16} />}>
              {t('tabArchive')}
              {archivedQuery.data &&
                ` (${(archivedQuery.data.events ?? []).filter(e => !HIDDEN_TYPES.has(e.type)).length})`}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Card
          p="sm"
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          style={{
            position: 'sticky',
            top: 'var(--app-shell-header-height, 60px)',
            zIndex: 100,
          }}
        >
          <Stack gap="sm">
            <MultiSelect
              label={t('filters.district')}
              placeholder={hasDistrictFilter ? undefined : districtPlaceholder}
              data={districtOptions}
              value={search.district ?? []}
              onChange={value =>
                updateSearchFilter({
                  district: value.length > 0 ? value : undefined,
                  // Reset the route filter — the route options change with the district set.
                  route: undefined,
                })
              }
              clearable
              searchable
              hidePickedOptions
              disabled={eventsQuery.isLoading || districtOptions.length === 0}
              w="100%"
            />
            <Group gap="sm" align="flex-end" wrap="wrap">
              <Select
                label={t('filters.route')}
                placeholder={t('filters.any')}
                data={routeOptions}
                value={search.route ?? null}
                onChange={value => updateSearchFilter({ route: value ?? undefined })}
                clearable
                searchable
                disabled={!hasDistrictFilter}
                w={240}
              />
              <Select
                label={t('filters.type')}
                placeholder={t('filters.any')}
                data={typeOptions}
                value={search.type ?? null}
                onChange={value => updateSearchFilter({ type: (value as UnexpectedEventType | null) ?? undefined })}
                clearable
                disabled={!hasDistrictFilter}
                w={280}
              />
              <Button
                variant="subtle"
                leftSection={<TbX size={14} />}
                disabled={!hasDistrictFilter && !search.route && !search.type}
                onClick={() => navigate({ to: '/unexpected-events', search: {}, replace: true })}
              >
                {t('filters.clear')}
              </Button>
            </Group>
          </Stack>
        </Card>

        <Box pos="relative" mih={200}>
          <LoadingOverlay visible={eventsQuery.isLoading} />

          {eventsQuery.isError && (
            <Alert color="red" icon={<TbAlertTriangle />}>
              {t('loadFailed')}
            </Alert>
          )}

          {!eventsQuery.isLoading && !eventsQuery.isError && visibleEvents.length === 0 && !hasDistrictFilter && (
            <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
              {noEventsMessage}
            </Text>
          )}

          {!eventsQuery.isLoading && !eventsQuery.isError && visibleEvents.length > 0 && !hasDistrictFilter && (
            <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
              {t('list.selectDistrictPrompt')}
            </Text>
          )}

          {hasDistrictFilter && filteredEvents.length === 0 && districtEvents.length > 0 && (
            <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
              {t('list.noFilteredEvents')}
            </Text>
          )}

          {hasDistrictFilter && districtEvents.length === 0 && !eventsQuery.isLoading && (
            <Text ta="center" c="dimmed" py="xl" fz="xl" fw={600}>
              {isArchive ? t('list.noArchivedEventsInDistrict') : t('list.noEventsInDistrict')}
            </Text>
          )}

          {hasDistrictFilter && filteredEvents.length > 0 && (
            <div style={canWrite ? LIST_GRID_WITH_CHECKBOX : LIST_GRID_STYLE}>
              {routeGroups.map(({ routeId, ids, events }, groupIdx) => {
                const allSelected = ids.every(id => selectedEvents.has(id))
                const isCollapsed = collapsedRoutes.has(routeId)
                return (
                  <Fragment key={routeId}>
                    {groupIdx > 0 && <Box style={FULL_SPAN} pt="md" />}
                    <Group justify="space-between" align="center" style={FULL_SPAN}>
                      <Group gap="xs" align="center">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() =>
                            setCollapsedRoutes(prev => {
                              const next = new Set(prev)
                              if (next.has(routeId)) next.delete(routeId)
                              else next.add(routeId)
                              return next
                            })
                          }
                          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                          {isCollapsed ? <TbChevronRight size={16} /> : <TbChevronDown size={16} />}
                        </ActionIcon>
                        <Title order={5} m={0}>
                          {t('filters.route')}
                        </Title>
                        <CopyableId value={routeId} size="md" monospace />
                        <Text size="sm" c="dimmed">
                          ({events.length})
                        </Text>
                      </Group>
                      {canWrite && (
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          onClick={() => toggleEventSelection(ids, !allSelected)}
                        >
                          {allSelected ? t('list.deselectAllInRoute') : t('list.selectAllInRoute')}
                        </Button>
                      )}
                    </Group>
                    <Divider style={FULL_SPAN} />
                    {!isCollapsed &&
                      events.map(enriched => (
                        <EventRow
                          key={enriched.id}
                          enriched={enriched}
                          selected={selectedEvents.has(enriched.id)}
                          canWrite={canWrite}
                          onToggle={handleRowToggle}
                        />
                      ))}
                  </Fragment>
                )
              })}
            </div>
          )}
        </Box>
      </Stack>
    </PageShell>
  )
}

type ResolveBulkPreviewProps = {
  events: ReadonlyArray<UnexpectedEventResponse>
  t: (key: string) => string
}

const ResolveBulkPreview = ({ events, t }: ResolveBulkPreviewProps) => (
  <Box mah={320} style={{ overflowY: 'auto' }}>
    <Stack gap={6}>
      {events.map(event => (
        <Group key={event.id} gap="sm" wrap="nowrap" align="center">
          <Badge color={TYPE_BADGE_COLOR[event.type]} variant="light" radius="sm" style={{ flexShrink: 0 }}>
            {t(TYPE_LABEL_KEY[event.type])}
          </Badge>
          <Text size="xs" w={130} style={{ flexShrink: 0 }}>
            {toPragueLocaleString(new Date(event.observed_at))}
          </Text>
          <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
            {event.route_id}
          </Text>
          <Text size="xs" ff="monospace" truncate="end" title={event.id} style={{ minWidth: 0, flex: 1 }}>
            {event.id}
          </Text>
        </Group>
      ))}
    </Stack>
  </Box>
)

type EventRowProps = {
  enriched: EnrichedEvent
  selected: boolean
  canWrite: boolean
  onToggle: (id: string, selected: boolean) => void
}

const EventRow = memo(({ enriched, selected, canWrite, onToggle }: EventRowProps) => {
  const { id, formattedObservedAt, typeLabel, badgeColor } = enriched
  const navigate = useNavigate()
  const [hovered, setHovered] = useState(false)
  const theme = useMantineTheme()
  const colorScheme = useComputedColorScheme('light')

  const selectedPaperStyle = useMemo((): CSSProperties => {
    if (colorScheme === 'dark') {
      return {
        ...ROW_PAPER_BASE,
        backgroundColor: alpha(theme.colors.blue[8], 0.35),
      }
    }
    return {
      ...ROW_PAPER_BASE,
      backgroundColor: theme.colors.blue[0],
    }
  }, [colorScheme, theme.colors.blue])

  const baseStyle = selected ? selectedPaperStyle : ROW_PAPER_BASE
  const style = hovered ? { ...baseStyle, ...ROW_PAPER_HOVER } : baseStyle

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('input[type="checkbox"]') || target.closest('button') || target.closest('a')) return
      navigate({
        to: '/unexpected-events/$eventId',
        params: { eventId: id },
        search: prev => prev,
      })
    },
    [navigate, id]
  )

  return (
    <Paper
      withBorder
      p="xs"
      radius="sm"
      style={{ ...style, ...ROW_SUBGRID }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCardClick}
    >
      {canWrite && (
        <Checkbox checked={selected} onChange={evt => onToggle(id, evt.currentTarget.checked)} aria-label={id} />
      )}
      <Badge color={badgeColor} variant="light" radius="sm" size="lg" style={{ justifySelf: 'start' }}>
        {typeLabel}
      </Badge>
      <Text size="sm">{formattedObservedAt}</Text>
      <CopyableId
        value={id}
        size="sm"
        monospace
        display={
          <Link
            to="/unexpected-events/$eventId"
            params={{ eventId: id }}
            search={KEEP_SEARCH}
            title={id}
            style={LINK_STYLE}
            onClick={e => e.stopPropagation()}
          >
            {id}
          </Link>
        }
      />
    </Paper>
  )
})
EventRow.displayName = 'EventRow'

export default UnexpectedEventsList
